import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail, clearStepUpToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { connectWS, onWS, sendWS } from "@/lib/chatws";
import { registerServiceWorker, enablePush } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { fetchMedia, getCached, seedLocal, nativeDownload } from "@/lib/mediacache";
import { uploadFile, createUploadController } from "@/lib/upload";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ready, setReady] = useState(false);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typingFrom, setTypingFrom] = useState(null);
  const [onlineIds, setOnlineIds] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadPaused, setUploadPaused] = useState(false);
  const uploadCtrlRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef({ mr: null, chunks: [], stream: null, timer: null, cancelled: false });
  const [replyTo, setReplyTo] = useState(null);
  const [pinned, setPinned] = useState([]);
  const [showStarred, setShowStarred] = useState(false);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [locked, setLocked] = useState(false);
  const [savingPk, setSavingPk] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [chatH, setChatH] = useState(null);
  const outerRef = useRef(null);

  // The chat must fit the VISIBLE viewport exactly so the message list (which has
  // its own scroll) is the only thing that scrolls — otherwise the whole page
  // scrolls and carries the header off the top. iOS reports a stale innerHeight on
  // resume/orientation, so we measure from visualViewport + the container's real
  // on-screen offset (auto-accounts for the top bar / any banner) and re-measure a
  // beat later to catch iOS's late update.
  useEffect(() => {
    const measure = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0); // reset any stray page scroll before measuring
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const top = outerRef.current ? Math.max(0, outerRef.current.getBoundingClientRect().top) : 0;
      setChatH(Math.max(240, Math.round(vh - top)));
    };
    const schedule = () => { measure(); requestAnimationFrame(measure); setTimeout(measure, 250); };
    schedule();
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    window.addEventListener("pageshow", schedule);
    window.addEventListener("focus", schedule);
    document.addEventListener("visibilitychange", schedule);
    const v = window.visualViewport;
    if (v) { v.addEventListener("resize", schedule); v.addEventListener("scroll", schedule); }
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      window.removeEventListener("pageshow", schedule);
      window.removeEventListener("focus", schedule);
      document.removeEventListener("visibilitychange", schedule);
      if (v) { v.removeEventListener("resize", schedule); v.removeEventListener("scroll", schedule); }
    };
  }, []);

  const activeRef = useRef(null);

  // Prevent the PAGE/window from scrolling while the chat is open (the Layout's
  // <main class="min-h-screen"> + 56px sticky top bar make the document taller
  // than the screen, which otherwise lets a swipe slide the header behind the top
  // bar). The inner message list still scrolls freely, so history is reachable.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    // Belt-and-braces: if anything (iOS overscroll bounce, programmatic scroll)
    // drifts the page, snap it straight back so the header can never slide away.
    const snapBack = () => { if (window.scrollY !== 0) window.scrollTo(0, 0); };
    window.addEventListener("scroll", snapBack, { passive: true });
    return () => {
      window.removeEventListener("scroll", snapBack);
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);
  const msgRefs = useRef({});

  const loadConvs = async () => {
    const { data } = await api.get("/chat/conversations");
    setConvs(data);
    setOnlineIds((prev) => {
      const next = { ...prev };
      data.forEach((c) => c.members.forEach((m) => { if (m.online) next[m.id] = true; }));
      return next;
    });
    return data;
  };

  useEffect(() => {
    (async () => {
      await loadConvs();
      connectWS();
      registerServiceWorker();
      setReady(true);
    })();
    const onVisible = () => { if (document.visibilityState === "visible") connectWS(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(() => {
    const off = onWS(async (data) => {
      if (data.type === "message") {
        if (activeRef.current && data.conversation_id === activeRef.current.id) {
          setMessages((prev) => [...prev, data.message]);
          api.post(`/chat/conversations/${activeRef.current.id}/read`).catch(() => {});
        } else {
          loadConvs();
        }
      } else if (data.type === "message_update") {
        if (activeRef.current && data.conversation_id === activeRef.current.id) {
          setMessages((prev) => prev.map((m) => (m.id === data.message.id ? { ...m, ...data.message } : m)));
          if (data.message.pinned !== undefined) loadPinned(activeRef.current.id);
        }
      } else if (data.type === "typing") {
        if (activeRef.current && data.conversation_id === activeRef.current.id) {
          setTypingFrom(data.user_id);
          clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingFrom(null), 3000);
        }
      } else if (data.type === "conversation_new") {
        loadConvs();
      } else if (data.type === "presence") {
        setOnlineIds((prev) => ({ ...prev, [data.user_id]: data.online }));
      } else if (data.type === "presence_snapshot") {
        setOnlineIds((prev) => {
          const next = { ...prev };
          (data.online || []).forEach((id) => { next[id] = true; });
          return next;
        });
      }
    });
    return off;
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typingFrom]);

  const loadPinned = async (cid) => {
    try { const { data } = await api.get(`/chat/conversations/${cid}/pinned`); setPinned(data); }
    catch { setPinned([]); }
  };

  const openConv = async (conv) => {
    setActive(conv);
    activeRef.current = conv;
    setMessages([]);
    setTypingFrom(null);
    setReplyTo(null);
    setShowStarred(false);
    setLocked(false);
    // Stricter: force a fresh passkey verification every time a protected chat is opened.
    if (conv.require_passkey) clearStepUpToken();
    try {
      const { data } = await api.get(`/chat/conversations/${conv.id}/messages`);
      setMessages(data);
      loadPinned(conv.id);
      api.post(`/chat/conversations/${conv.id}/read`).catch(() => {});
      setConvs((prev) => prev.map((c) => (c.id === conv.id ? { ...c, unread: 0 } : c)));
    } catch (e) {
      if (e.response?.status === 428) setLocked(true);
      else toast.error("Could not open conversation");
    }
  };

  const togglePasskey = async (val) => {
    if (!active) return;
    setSavingPk(true);
    try {
      const { data } = await api.patch(`/chat/conversations/${active.id}/passkey`, { require_passkey: val });
      setActive(data);
      activeRef.current = data;
      setConvs((prev) => prev.map((c) => (c.id === data.id ? { ...c, require_passkey: data.require_passkey } : c)));
      toast.success(val ? "Passkey now required to open this chat" : "Passkey requirement removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update setting");
    } finally {
      setSavingPk(false);
    }
  };

  const toggleStarred = async () => {
    if (!active) return;
    const next = !showStarred;
    setShowStarred(next);
    const { data } = await api.get(`/chat/conversations/${active.id}/messages${next ? "?starred=1" : ""}`);
    setMessages(data);
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`,
        { text: text.trim(), reply_to_id: replyTo?.id || null });
      setMessages((prev) => [...prev, data]);
      setText("");
      setReplyTo(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const react = async (m, emoji) => {
    try { await api.post(`/chat/messages/${m.id}/react`, { emoji }); }
    catch { toast.error("Could not react"); }
  };
  const toggleStar = async (m) => {
    try {
      const { data } = await api.post(`/chat/messages/${m.id}/star`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, starred: data.starred } : x)));
      toast.success(data.starred ? "Starred" : "Removed star");
    } catch { toast.error("Could not star"); }
  };
  const togglePin = async (m) => {
    try {
      const { data } = await api.post(`/chat/messages/${m.id}/pin`);
      toast.success(data.pinned ? "Pinned" : "Unpinned");
      loadPinned(active.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not pin"); }
  };
  const copyMsg = async (m) => {
    try { await navigator.clipboard.writeText(m.text || ""); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  const scrollToMsg = (id) => {
    const el = msgRefs.current[id];
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-[#1B4332]"); setTimeout(() => el.classList.remove("ring-2", "ring-[#1B4332]"), 1500); }
  };
  const doForward = async (targetConvId) => {
    if (!forwardMsg) return;
    try {
      await api.post(`/chat/conversations/${targetConvId}/messages`,
        { text: forwardMsg.text || "", attachment: forwardMsg.attachment || null, forwarded: true });
      toast.success("Forwarded");
      setForwardMsg(null);
      if (active?.id === targetConvId) openConv(active);
    } catch { toast.error("Forward failed"); }
  };

  const onType = (v) => {
    setText(v);
    if (active) sendWS({ type: "typing", conversation_id: active.id });
  };

  const sendAttachment = async (file) => {
    if (!active) return;
    if (file.size > 2 * 1024 * 1024 * 1024) return toast.error("Max 2GB");
    const ctrl = createUploadController();
    uploadCtrlRef.current = ctrl;
    setUploadPaused(false);
    setSending(true);
    setUploadPct(0);
    try {
      const up = await uploadFile(file, { controller: ctrl, onProgress: (p) => setUploadPct(Math.round(p * 100)) });
      const attachment = { file_id: up.id, name: file.name, type: file.type, size: file.size };
      // Optimistic preview: show the image instantly from the local file (no download).
      if ((file.type || "").startsWith("image/")) {
        seedLocal(up.id, URL.createObjectURL(file));
      }
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, { text: text.trim() || "", attachment });
      setMessages((prev) => [...prev, data]);
      setText("");
    } catch (e) {
      if (ctrl.signal.aborted) toast("Upload cancelled");
      else toast.error("Attachment failed");
    } finally {
      uploadCtrlRef.current = null;
      setSending(false);
      setUploadPct(null);
      setUploadPaused(false);
    }
  };

  const togglePauseUpload = () => {
    const ctrl = uploadCtrlRef.current;
    if (!ctrl) return;
    if (ctrl.paused) { ctrl.resume(); setUploadPaused(false); }
    else { ctrl.pause(); setUploadPaused(true); }
  };
  const cancelUpload = () => uploadCtrlRef.current?.cancel();

  const pickAudioMime = () => {
    const opts = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
    for (const t of opts) { if (window.MediaRecorder?.isTypeSupported?.(t)) return t; }
    return "";
  };

  const startRecording = async () => {
    if (!active) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const st = recRef.current;
      st.mr = mr; st.chunks = []; st.stream = stream; st.cancelled = false;
      mr.ondataavailable = (e) => e.data.size && st.chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(st.timer);
        if (!st.cancelled && st.chunks.length) {
          const blob = new Blob(st.chunks, { type: mr.mimeType || "audio/webm" });
          sendVoice(blob, mr.mimeType || "audio/webm");
        }
      };
      mr.start();
      setRecording(true);
      setRecSecs(0);
      st.timer = setInterval(() => setRecSecs((s) => {
        if (s >= 300) { stopRecording(true); return s; }  // 5 min cap
        return s + 1;
      }), 1000);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = (send) => {
    const st = recRef.current;
    st.cancelled = !send;
    setRecording(false);
    try { st.mr && st.mr.state !== "inactive" && st.mr.stop(); } catch {}
  };

  const sendVoice = async (blob, mime) => {
    if (!active) return;
    setSending(true);
    try {
      const ext = mime.includes("mp4") || mime.includes("aac") ? "m4a" : "webm";
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mime });
      const up = await uploadFile(file);
      const attachment = { file_id: up.id, name: file.name, type: mime, size: file.size, voice: true, duration: recSecs };
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, { text: "", attachment });
      setMessages((prev) => [...prev, data]);
    } catch {
      toast.error("Could not send voice message");
    } finally {
      setSending(false);
    }
  };

  const openImagePreview = (url, name) => setLightbox({ url, name, owned: false });

  const openAttachment = async (att) => {
    try {
      if ((att.type || "").startsWith("image/")) {
        const url = await fetchMedia(att.file_id, { thumb: false });
        setLightbox({ url, name: att.name, type: att.type, owned: true });
        return;
      }
      // Non-image: native browser download (streams to disk, reliable on iOS + large files).
      await nativeDownload(att.file_id, { inline: false });
    } catch { toast.error("Could not open attachment"); }
  };

  const convTitle = (c) => c.type === "channel" ? c.name : (c.members.find((m) => m.id !== user.id)?.name || "Direct message");
  const convOnline = (c) => c.type === "dm" && !!onlineIds[c.members.find((m) => m.id !== user.id)?.id];

  const doEnablePush = async () => {
    try {
      const r = await enablePush();
      if (r.ok) toast.success("Push notifications enabled");
      else if (r.reason === "denied") toast.error("Notifications blocked. Enable them in Settings for this app.");
      else if (r.reason === "unsupported") toast.error("On iPhone: add to Home Screen and open from there (needs iOS 16.4+).");
      else if (r.reason === "no-vapid") toast.error("Notifications aren't configured on the server yet. Contact your admin.");
      else toast.error(r.message ? `Push failed: ${r.message}` : "Could not enable push");
    } catch (e) {
      toast.error("Could not enable push on this browser");
    }
  };

  if (!ready) return <div className="p-8 text-muted-foreground">Setting up secure chat…</div>;

  return (
    <div ref={outerRef} className="flex overflow-hidden" style={{ height: chatH ? `${chatH}px` : "calc(100dvh - 3.5rem)" }}>
      {/* Conversation list */}
      <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 border-r border-gray-200 bg-white flex-col`}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-extrabold tracking-tight">Messages</h1>
            <p className="text-[11px] text-gray-400 flex items-center gap-1"><Icons.Lock className="w-3 h-3" /> Encrypted &amp; private</p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={doEnablePush} title="Enable notifications" data-testid="enable-push-btn"><Icons.Bell className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setDialogOpen(true)} title="New conversation" data-testid="new-conversation-btn"><Icons.SquarePen className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto vault-scroll">
          {convs.length === 0 && <div className="p-8 text-center text-sm text-gray-400">No conversations yet. Start one with the compose button.</div>}
          {convs.map((c) => (
            <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => openConv(c)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-gray-50 hover:bg-gray-50 ${active?.id === c.id ? "bg-gray-50" : ""}`}>
              <div className="relative w-10 h-10 rounded-full bg-[#1B4332] text-white flex items-center justify-center font-semibold shrink-0">
                {c.type === "channel" ? <Icons.Hash className="w-5 h-5" /> : convTitle(c)[0]?.toUpperCase()}
                {convOnline(c) && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {c.require_passkey && <Icons.Lock className="w-3 h-3 text-[#1B4332] shrink-0" data-testid={`conv-lock-${c.id}`} />}
                  <span className="truncate">{convTitle(c)}</span>
                </div>
                <div className="text-xs text-gray-400 truncate">{c.type === "channel" ? `${c.members.length} members` : (c.members.find((m) => m.id !== user.id)?.email || "")}</div>
              </div>
              {c.unread > 0 && <span className="ml-auto bg-[#1B4332] text-white text-[11px] rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center" data-testid={`unread-${c.id}`}>{c.unread}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className={`${active ? "flex" : "hidden md:flex"} flex-1 flex-col bg-[#F9FAFB] min-w-0 min-h-0 overflow-hidden`}>
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Icons.MessagesSquare className="w-10 h-10" />
            <p className="text-sm">Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="h-16 shrink-0 border-b border-gray-200 bg-white flex items-center gap-3 px-4">
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => { setActive(null); activeRef.current = null; }}><Icons.ChevronLeft className="w-5 h-5" /></Button>
              <div className="w-9 h-9 rounded-full bg-[#1B4332] text-white flex items-center justify-center font-semibold">
                {active.type === "channel" ? <Icons.Hash className="w-4 h-4" /> : convTitle(active)[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{convTitle(active)}</div>
                <div className="text-[11px] text-gray-400 truncate">{active.type === "channel" ? active.members.map((m) => m.name).join(", ") : (convOnline(active) ? "Online" : "Offline")}</div>
              </div>
              <Button variant="ghost" size="icon" title="Starred" data-testid="starred-toggle"
                onClick={toggleStarred} className={showStarred ? "text-amber-500" : "text-gray-400"}>
                <Icons.Star className="w-5 h-5" fill={showStarred ? "currentColor" : "none"} />
              </Button>
              {active.created_by === user.id ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" title="Chat security" data-testid="chat-security-btn"
                      className={active.require_passkey ? "text-[#1B4332]" : "text-gray-400"}>
                      {active.require_passkey ? <Icons.Lock className="w-5 h-5" /> : <Icons.Unlock className="w-5 h-5" />}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-4" align="end" data-testid="chat-security-popover">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5"><Icons.ShieldCheck className="w-4 h-4 text-[#1B4332]" /> Require passkey</div>
                        <p className="text-[11px] text-gray-500 mt-1">Members must verify with a passkey every time they open this chat. Only you (the creator) can change this.</p>
                      </div>
                      <Switch checked={!!active.require_passkey} disabled={savingPk} onCheckedChange={togglePasskey} data-testid="require-passkey-switch" />
                    </div>
                  </PopoverContent>
                </Popover>
              ) : active.require_passkey ? (
                <div title="Passkey protected" data-testid="chat-locked-indicator" className="text-[#1B4332] p-2"><Icons.Lock className="w-5 h-5" /></div>
              ) : null}
            </div>

            {locked ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 p-6" data-testid="chat-locked-screen">
                <Icons.ShieldAlert className="w-10 h-10 text-amber-500" />
                <p className="text-sm font-semibold text-gray-700">This conversation is protected</p>
                <p className="text-xs text-gray-500 max-w-xs">Verify with your passkey to view messages. You'll be asked each time you open this chat.</p>
                <Button onClick={() => openConv(active)} className="bg-[#1B4332] hover:bg-[#14472F]" data-testid="chat-verify-btn">
                  <Icons.Fingerprint className="w-4 h-4 mr-1.5" /> Verify with passkey
                </Button>
              </div>
            ) : (
            <>
            {pinned.length > 0 && !showStarred && (
              <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-4 py-2 space-y-1" data-testid="pinned-bar">
                {pinned.map((p) => (
                  <button key={p.id} onClick={() => scrollToMsg(p.id)} className="flex items-center gap-2 w-full text-left text-xs text-amber-900 hover:underline">
                    <Icons.Pin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{p.text || (p.attachment ? p.attachment.name : "Message")}</span>
                    {isAdmin && <Icons.X className="w-3.5 h-3.5 ml-auto shrink-0 hover:text-red-600" onClick={(e) => { e.stopPropagation(); togglePin(p); }} />}
                  </button>
                ))}
              </div>
            )}

            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain vault-scroll p-4 space-y-2">
              {showStarred && messages.length === 0 && <div className="text-center text-sm text-gray-400 mt-6">No starred messages</div>}
              {messages.map((m) => (
                <MessageBubble key={m.id} m={m} mine={m.sender_id === user.id} isAdmin={isAdmin}
                  channel={active.type === "channel"}
                  sender={active.members.find((x) => x.id === m.sender_id)}
                  senderName={(id) => active.members.find((x) => x.id === id)?.name || "Someone"}
                  bindRef={(el) => { if (el) msgRefs.current[m.id] = el; }}
                  onReact={react} onReply={setReplyTo} onForward={setForwardMsg} onCopy={copyMsg}
                  onStar={toggleStar} onPin={togglePin} onOpenAttachment={openAttachment}
                  onImagePreview={openImagePreview}
                  onQuoteClick={scrollToMsg} />
              ))}
              {typingFrom && <div className="text-xs text-gray-400 italic px-2">typing…</div>}
            </div>

            {replyTo && (
              <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-2 flex items-center gap-2" data-testid="reply-preview">
                <div className="w-1 self-stretch bg-[#1B4332] rounded" />
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold text-[#1B4332]">Replying to {active.members.find((x) => x.id === replyTo.sender_id)?.name || "message"}</div>
                  <div className="text-xs text-gray-500 truncate">{replyTo.text || "Attachment"}</div>
                </div>
                <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-gray-200 rounded" data-testid="cancel-reply"><Icons.X className="w-4 h-4 text-gray-500" /></button>
              </div>
            )}

            {uploadPct !== null && (
              <div className="shrink-0 px-3 pt-2 bg-white" data-testid="chat-upload-progress">
                <div className="flex items-center gap-2 text-xs text-[#1B4332]">
                  {uploadPaused ? <Icons.PauseCircle className="w-3.5 h-3.5" /> : <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <span>{uploadPaused ? "Paused" : "Uploading…"} {uploadPct}%</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={togglePauseUpload} className="p-1 rounded hover:bg-gray-100" title={uploadPaused ? "Resume" : "Pause"} data-testid="upload-pause-btn">
                      {uploadPaused ? <Icons.Play className="w-3.5 h-3.5" /> : <Icons.Pause className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={cancelUpload} className="p-1 rounded hover:bg-gray-100 text-red-500" title="Cancel" data-testid="upload-cancel-btn">
                      <Icons.X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-[#1B4332] transition-[width] duration-200" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            )}
            <div className="shrink-0 border-t border-gray-200 bg-white p-3 flex items-center gap-2">
              {recording ? (
                <div className="flex-1 flex items-center gap-3" data-testid="recording-bar">
                  <button onClick={() => stopRecording(false)} className="p-2 rounded-md hover:bg-gray-100 text-red-500" title="Cancel" data-testid="cancel-recording-btn">
                    <Icons.Trash2 className="w-5 h-5" />
                  </button>
                  <span className="flex items-center gap-2 text-sm text-red-500 font-medium">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                    Recording {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, "0")}
                  </span>
                  <div className="flex-1" />
                  <Button onClick={() => stopRecording(true)} className="bg-[#1B4332] hover:bg-[#14472F]" data-testid="send-recording-btn">
                    <Icons.Send className="w-4 h-4 mr-1.5" /> Send
                  </Button>
                </div>
              ) : (
                <>
                  <label className="p-2 rounded-md hover:bg-gray-100 cursor-pointer" title="Attach file">
                    <Icons.Paperclip className="w-5 h-5 text-gray-500" />
                    <input type="file" className="hidden" data-testid="chat-file-input" disabled={sending} onChange={(e) => e.target.files[0] && sendAttachment(e.target.files[0])} />
                  </label>
                  <Input value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                    placeholder="Message…" data-testid="chat-input" />
                  {text.trim() ? (
                    <Button onClick={send} disabled={sending} className="bg-[#1B4332] hover:bg-[#14472F]" data-testid="chat-send-btn"><Icons.Send className="w-4 h-4" /></Button>
                  ) : (
                    <Button onClick={startRecording} disabled={sending} className="bg-[#1B4332] hover:bg-[#14472F]" title="Record voice message" data-testid="record-voice-btn"><Icons.Mic className="w-4 h-4" /></Button>
                  )}
                </>
              )}
            </div>
            </>
            )}
          </>
        )}
      </div>

      <NewConversationDialog open={dialogOpen} onClose={() => setDialogOpen(false)} me={user}
        onCreated={async (c) => { await loadConvs(); openConv(c); }} />
      <ForwardDialog msg={forwardMsg} convs={convs} me={user} onClose={() => setForwardMsg(null)} onForward={doForward} />
      <Dialog open={!!lightbox} onOpenChange={(o) => { if (!o && lightbox) { if (lightbox.owned) URL.revokeObjectURL(lightbox.url); setLightbox(null); } }}>
        <DialogContent className="max-w-3xl p-3" data-testid="image-lightbox">
          <DialogHeader><DialogTitle className="text-sm truncate pr-6">{lightbox?.name || "Image"}</DialogTitle></DialogHeader>
          {lightbox && <img src={lightbox.url} alt={lightbox.name} className="w-full max-h-[72vh] object-contain rounded" data-testid="lightbox-image" />}
          {lightbox && (
            <div className="flex justify-end">
              <a href={lightbox.url} download={lightbox.name} data-testid="lightbox-download"
                className="text-sm text-[#1B4332] font-medium inline-flex items-center gap-1.5 hover:underline">
                <Icons.Download className="w-4 h-4" /> Download
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MessageBubble({ m, mine, isAdmin, channel, sender, senderName, bindRef, onReact, onReply, onForward, onCopy, onStar, onPin, onOpenAttachment, onImagePreview, onQuoteClick }) {
  const [open, setOpen] = useState(false);
  const lpTimer = useRef(null);
  const startLP = () => { lpTimer.current = setTimeout(() => setOpen(true), 450); };
  const cancelLP = () => clearTimeout(lpTimer.current);
  const reactions = Object.entries(m.reactions || {}).filter(([, u]) => u.length > 0);

  const action = (fn) => () => { setOpen(false); fn(); };

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} group`}>
      <div className="max-w-[78%] flex flex-col items-stretch">
        <div ref={bindRef}
          onTouchStart={startLP} onTouchEnd={cancelLP} onTouchMove={cancelLP}
          className={`relative rounded-2xl px-3.5 py-2 transition-shadow ${mine ? "bg-[#1B4332] text-white rounded-br-sm" : "bg-white border border-gray-200 rounded-bl-sm"}`}
          data-testid="chat-message">
          {channel && !mine && <div className="text-[11px] font-semibold text-[#1B4332] mb-0.5">{sender?.name}</div>}
          {m.forwarded && <div className={`text-[10px] italic mb-0.5 flex items-center gap-1 ${mine ? "text-white/60" : "text-gray-400"}`}><Icons.CornerUpRight className="w-3 h-3" />Forwarded</div>}
          {m.reply_to && (
            <button onClick={() => onQuoteClick(m.reply_to.id)} className={`block w-full text-left mb-1 rounded px-2 py-1 border-l-2 ${mine ? "bg-white/10 border-white/50" : "bg-gray-50 border-[#1B4332]"}`}>
              <div className={`text-[10px] font-semibold ${mine ? "text-white/80" : "text-[#1B4332]"}`}>{senderName(m.reply_to.sender_id)}</div>
              <div className={`text-[11px] truncate ${mine ? "text-white/70" : "text-gray-500"}`}>{m.reply_to.text || "Attachment"}</div>
            </button>
          )}
          {m.attachment && (
            <MessageAttachment att={m.attachment} mine={mine} onOpen={onOpenAttachment} />
          )}
          {m.text && m.text.length > 0 && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
          <div className={`flex items-center gap-1 justify-end text-[10px] mt-0.5 ${mine ? "text-white/60" : "text-gray-400"}`}>
            {m.starred && <Icons.Star className="w-3 h-3 text-amber-400" fill="currentColor" />}
            {m.pinned && <Icons.Pin className="w-3 h-3" />}
            {fmtTime(m.created_at)}
          </div>

          {/* Desktop hover trigger + shared popover */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button data-testid={`msg-menu-${m.id}`}
                className={`absolute top-1 ${mine ? "-left-7" : "-right-7"} opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full bg-white border border-gray-200 shadow-sm text-gray-500`}>
                <Icons.MoreHorizontal className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-1.5" align={mine ? "end" : "start"}>
              <div className="flex justify-between px-1 pb-1.5 mb-1 border-b">
                {QUICK_REACTIONS.map((e) => (
                  <button key={e} onClick={action(() => onReact(m, e))} data-testid={`react-${e}`}
                    className="text-lg hover:scale-125 transition-transform">{e}</button>
                ))}
              </div>
              <MenuItem icon="Reply" label="Reply" testid={`reply-${m.id}`} onClick={action(() => onReply(m))} />
              <MenuItem icon="CornerUpRight" label="Forward" testid={`forward-${m.id}`} onClick={action(() => onForward(m))} />
              {m.text && <MenuItem icon="Copy" label="Copy" testid={`copy-${m.id}`} onClick={action(() => onCopy(m))} />}
              <MenuItem icon="Star" label={m.starred ? "Unstar" : "Star"} testid={`star-${m.id}`} onClick={action(() => onStar(m))} />
              {isAdmin && <MenuItem icon="Pin" label={m.pinned ? "Unpin" : "Pin"} testid={`pin-${m.id}`} onClick={action(() => onPin(m))} />}
            </PopoverContent>
          </Popover>
        </div>

        {reactions.length > 0 && (
          <div className={`flex gap-1 mt-0.5 flex-wrap ${mine ? "justify-end" : "justify-start"}`} data-testid={`reactions-${m.id}`}>
            {reactions.map(([e, u]) => (
              <button key={e} onClick={() => onReact(m, e)}
                className="text-[11px] bg-white border border-gray-200 rounded-full px-1.5 py-0.5 shadow-sm hover:bg-gray-50">
                {e} {u.length}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VoicePlayer({ att, mine }) {
  const audioRef = useRef(null);
  const [url, setUrl] = useState(() => getCached(att.file_id, false) || null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(att.duration || 0);
  const [wantPlay, setWantPlay] = useState(false);

  const toggle = async () => {
    if (!url) {
      const src = await fetchMedia(att.file_id, { thumb: false });
      setUrl(src);
      setWantPlay(true);
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause(); else a.play();
  };

  useEffect(() => {
    if (url && wantPlay && audioRef.current) { audioRef.current.play(); setWantPlay(false); }
  }, [url, wantPlay]);

  const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;
  const accent = mine ? "bg-white" : "bg-[#1B4332]";
  const track = mine ? "bg-white/30" : "bg-gray-300";

  return (
    <div className="flex items-center gap-3 mb-1 w-[220px]" data-testid="voice-message">
      <button onClick={toggle} data-testid="voice-play-btn"
        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mine ? "bg-white text-[#1B4332]" : "bg-[#1B4332] text-white"}`}>
        {playing ? <Icons.Pause className="w-4 h-4" /> : <Icons.Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`h-1.5 rounded-full ${track} overflow-hidden`}>
          <div className={`h-full ${accent} transition-[width]`} style={{ width: `${pct}%` }} />
        </div>
        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-gray-400"}`}>{fmt(playing || cur ? cur : dur)}</div>
      </div>
      {url && (
        <audio ref={audioRef} src={url} preload="metadata"
          onLoadedMetadata={(e) => isFinite(e.target.duration) && setDur(e.target.duration)}
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setCur(0); }}
          onTimeUpdate={(e) => setCur(e.target.currentTime)} />
      )}
    </div>
  );
}

function MessageAttachment({ att, mine, onOpen }) {
  const isImage = (att.type || "").startsWith("image/");
  const isVoice = att.voice || (att.type || "").startsWith("audio/");
  const [imgUrl, setImgUrl] = useState(() => (isImage ? getCached(att.file_id, true) || null : null));
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isImage || imgUrl) return;
    let cancelled = false;
    fetchMedia(att.file_id, { thumb: true })
      .then((url) => { if (!cancelled) setImgUrl(url); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [att.file_id, isImage, imgUrl]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onOpen(att);
    } finally {
      setTimeout(() => setDownloading(false), 800);
    }
  };

  if (isVoice) return <VoicePlayer att={att} mine={mine} />;

  if (isImage) {
    return (
      <button onClick={() => onOpen(att)} data-testid={imgUrl ? "chat-attachment-image" : "chat-attachment-loading"}
        className="relative block mb-1 rounded-xl overflow-hidden max-w-[240px] w-[240px] bg-gradient-to-br from-gray-200 to-gray-100">
        {imgUrl && !failed ? (
          <img src={imgUrl} alt={att.name}
            onLoad={() => setLoaded(true)}
            className={`w-full h-auto max-h-72 object-cover transition-[filter,opacity] duration-300 ${loaded ? "blur-0 opacity-100" : "blur-md opacity-70"}`} />
        ) : (
          <div className="w-full h-40 flex items-center justify-center">
            {failed ? <span className="text-xs text-gray-400">Image unavailable</span>
                    : <div className="w-full h-full animate-pulse bg-gradient-to-br from-gray-200 to-gray-300" />}
          </div>
        )}
      </button>
    );
  }

  return (
    <button onClick={handleDownload} data-testid="chat-attachment" disabled={downloading}
      className={`flex items-center gap-2 mb-1 rounded-lg px-3 py-2 border ${mine ? "border-white/25 bg-white/10 text-white" : "border-gray-200 bg-gray-50 text-[#1B4332]"}`}>
      {downloading ? <Icons.Loader2 className="w-5 h-5 shrink-0 animate-spin" /> : <Icons.FileText className="w-5 h-5 shrink-0" />}
      <span className="min-w-0">
        <span className="block text-sm font-medium truncate max-w-[180px]">{att.name}</span>
        <span className={`block text-[10px] ${mine ? "text-white/60" : "text-gray-400"}`}>
          {downloading ? "Downloading…" : "Tap to download"}
        </span>
      </span>
    </button>
  );
}

function MenuItem({ icon, label, onClick, testid }) {
  const C = Icons[icon] || Icons.Circle;
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-2.5 px-2.5 py-1.5 text-sm rounded-md hover:bg-gray-100 text-gray-700">
      <C className="w-4 h-4 text-gray-500" /> {label}
    </button>
  );
}

function ForwardDialog({ msg, convs, me, onClose, onForward }) {
  return (
    <Dialog open={!!msg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="forward-dialog">
        <DialogHeader><DialogTitle>Forward to…</DialogTitle></DialogHeader>
        <div className="max-h-[360px] overflow-y-auto vault-scroll -mx-1 px-1 divide-y divide-gray-50">
          {convs.map((c) => {
            const title = c.type === "channel" ? c.name : (c.members.find((m) => m.id !== me.id)?.name || "Direct message");
            return (
              <button key={c.id} onClick={() => onForward(c.id)} data-testid={`forward-to-${c.id}`}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-gray-50 px-1 rounded">
                <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center text-sm font-semibold">
                  {c.type === "channel" ? <Icons.Hash className="w-4 h-4" /> : title[0]?.toUpperCase()}
                </div>
                <span className="text-sm truncate">{title}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewConversationDialog({ open, onClose, me, onCreated }) {
  const [tab, setTab] = useState("dm");
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [channelName, setChannelName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/chat/members").then((r) => setMembers(r.data));
      setSelected([]); setChannelName(""); setTab("dm");
    }
  }, [open]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const create = async () => {
    const ids = tab === "dm" ? selected.slice(0, 1) : selected;
    if (ids.length === 0) return toast.error("Select at least one member");
    if (tab === "channel" && !channelName.trim()) return toast.error("Channel name required");
    setBusy(true);
    try {
      const { data } = await api.post("/chat/conversations", {
        type: tab, name: tab === "channel" ? channelName.trim() : null,
        members: [...new Set([me.id, ...ids])],
      });
      onClose();
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-conversation-dialog">
        <DialogHeader><DialogTitle className="font-heading">New conversation</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected([]); }}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="dm" data-testid="tab-dm">Direct message</TabsTrigger>
            <TabsTrigger value="channel" data-testid="tab-channel">Channel</TabsTrigger>
          </TabsList>
          <TabsContent value="channel" className="pt-3">
            <Label>Channel name</Label>
            <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} placeholder="e.g. Leadership" className="mt-1.5" data-testid="channel-name-input" />
          </TabsContent>
        </Tabs>
        <div className="max-h-64 overflow-y-auto vault-scroll border border-gray-100 rounded-md mt-2">
          {members.length === 0 && <div className="p-6 text-center text-sm text-gray-400">No other members yet.</div>}
          {members.map((m) => (
            <label key={m.id} className={`flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${!m.has_key ? "opacity-50" : ""}`} data-testid={`member-${m.id}`}>
              <Checkbox checked={selected.includes(m.id)} disabled={!m.has_key || (tab === "dm" && selected.length >= 1 && !selected.includes(m.id))} onCheckedChange={() => toggle(m.id)} />
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold">{m.name?.[0]?.toUpperCase()}</div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
                <div className="text-[11px] text-gray-400 truncate">{m.has_key ? m.email : "No encryption key yet"}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy} className="bg-[#1B4332] hover:bg-[#14472F]" data-testid="create-conversation-btn">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
